/**
 * Maps thrown / caught errors into copy safe to show in the Studio UI.
 * Technical messages (network failures, hostnames, HTTP dumps) become a
 * friendly headline with the raw message in `detail` for support/debug.
 */

export const DEFAULT_USER_ERROR_HEADLINE =
  "Oops — something went wrong. Refresh and try again.";

export type UserFacingError = {
  headline: string;
  detail?: string;
};

type MaybeDecoratedError = {
  userMessage?: string;
  technicalDetail?: string;
  message?: string;
};

/** Raw operator-facing message from an unknown throw value. */
export function rawErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Unknown error";
}

/** Browser / SDK network failures (e.g. `Failed to fetch (api.example.com)`). */
export function isNetworkFetchError(message: string): boolean {
  const m = message.trim();
  if (!m) return false;
  return (
    /^failed to fetch\b/i.test(m) ||
    /^networkerror\b/i.test(m) ||
    /^network request failed\b/i.test(m) ||
    /^load failed\b/i.test(m)
  );
}

/** Messages that look like debug output, not end-user copy. */
export function looksTechnical(message: string): boolean {
  const m = message.trim();
  if (!m) return false;
  if (isNetworkFetchError(m)) return true;
  if (/https?:\/\//i.test(m)) return true;
  if (/\b[a-z0-9-]+\.(com|io|net|org|local)\b/i.test(m)) return true;
  if (/\breturned\s+\d{3}\b/i.test(m)) return true;
  if (/^ERROR\s*\|/i.test(m)) return true;
  if (/\bfailed\s*\([^)]+\)/i.test(m)) return true;
  if (/^[a-z0-9-]+ returned \d{3}/i.test(m)) return true;
  return false;
}

/**
 * 2026-07-09 (operator: "the user should never hear of slug"):
 * the server's `pitch-write` handler returns 409 with `{"error":
 * "Slug already exists"}` when the auto-generated URL slug
 * collides with an existing row. `slug` is a database-internal
 * field name and should never appear in user-facing copy.
 *
 * Detection: any of the known slug-collision phrases from
 * `pitch-write` and `normalizeCreatePitchInput`, wrapped or
 * unwrapped (e.g. `Creating draft failed: Slug already exists`).
 *
 * Output policy: BOTH `headline` and `detail` are slug-free so the
 * `UserFacingErrorBlock` never surfaces the database term to the
 * user. The raw server message is captured by the orchestrator
 * into a Sentry breadcrumb (via `Sentry.addBreadcrumb` /
 * `reportError`) so support can still diagnose without it ever
 * reaching the user-visible UI.
 */
const SLUG_CONFLICT_REGEX =
  /\b(slug|slugs)\b[^.\n]*\b(already\s+(exists?|taken|in\s+use)|is\s+already\s+(exists?|taken)|exists?)\b|\b(slug|slugs)\b\s+(already\s+)?(exists?|taken)/i;

export function isSlugCollisionError(message: string): boolean {
  const m = message.trim();
  if (!m) return false;
  return SLUG_CONFLICT_REGEX.test(m);
}

export const SLUG_CONFLICT_HEADLINE =
  "A pitch with this title already exists. Try a slightly different title.";

export const SLUG_CONFLICT_DETAIL =
  "Pick a title that's unique enough to land on its own URL, and we'll generate a fresh URL for your pitch.";

export function isAlreadyUserFriendly(message: string): boolean {
  const m = message.trim();
  if (!m) return false;
  return !looksTechnical(m);
}

/**
 * Prefer `err.userMessage` (from `invokeFunction` decoration), then
 * pass through already-friendly copy, otherwise headline + optional detail.
 */
export function toUserFacingError(
  err: unknown,
  opts: { fallbackHeadline?: string } = {},
): UserFacingError {
  const fallback = opts.fallbackHeadline ?? DEFAULT_USER_ERROR_HEADLINE;
  const anyErr = err as MaybeDecoratedError | null;

  if (anyErr?.userMessage && typeof anyErr.userMessage === "string") {
    const detail =
      typeof anyErr.technicalDetail === "string" && anyErr.technicalDetail
        ? anyErr.technicalDetail
        : anyErr.message && anyErr.message !== anyErr.userMessage
          ? anyErr.message
          : undefined;
    return detail ? { headline: anyErr.userMessage, detail } : { headline: anyErr.userMessage };
  }

  const raw = rawErrorMessage(err);
  if (!raw || raw === "Unknown error") {
    return { headline: fallback };
  }

  // 2026-07-09 (operator: "the user should never hear of slug"):
  // server-side slug-collision errors leak a database-internal field
  // name into the user-visible headline ("Slug already exists"). The
  // message *would* pass the `isAlreadyUserFriendly` check (no
  // hostname, no HTTP dump, no debug fragments), so it surfaces as-is
  // and the user sees the slug term. Detect the collision shape here,
  // BEFORE the pass-through, and rewrite the headline so the user
  // sees actionable copy instead.
  if (isSlugCollisionError(raw)) {
    // Strip the slug term from BOTH fields. The `UserFacingErrorBlock`
    // surfaces `detail` to the user as muted secondary copy, so
    // letting the raw server message through here would still leak
    // "slug" into the visible UI. The raw message lives in the
    // orchestrator's Sentry breadcrumb / reportError payload, which
    // is the diagnostic surface for support. See
    // `PitchWizardV3.vue#onPublish`.
    return { headline: SLUG_CONFLICT_HEADLINE, detail: SLUG_CONFLICT_DETAIL };
  }

  if (isAlreadyUserFriendly(raw)) {
    return { headline: raw };
  }

  return { headline: fallback, detail: raw };
}

/** Flat string for legacy call sites that only accept a single line. */
export function userFacingErrorHeadline(err: unknown, fallback?: string): string {
  return toUserFacingError(err, fallback ? { fallbackHeadline: fallback } : undefined).headline;
}

/** Wrap literal validation / auth copy. */
export function userFacingMessage(headline: string): UserFacingError {
  return { headline };
}

export type DisplayError = UserFacingError | string | null;

export function normalizeDisplayError(value: DisplayError): UserFacingError | null {
  if (!value) return null;
  if (typeof value === "string") return toUserFacingError(value);
  return value;
}

/** Action headline when the underlying error is technical (e.g. save / upload). */
export function toUserFacingErrorForAction(
  err: unknown,
  actionHeadline: string,
): UserFacingError {
  const facing = toUserFacingError(err);
  if (facing.detail) {
    return { headline: actionHeadline, detail: facing.detail };
  }
  const raw = rawErrorMessage(err);
  if (looksTechnical(raw)) {
    return { headline: actionHeadline, detail: raw };
  }
  return facing;
}

/** Single-line toast copy; technical detail in parentheses when present. */
export function userFacingErrorToText(err: UserFacingError): string {
  return err.detail ? `${err.headline} (${err.detail})` : err.headline;
}
